<template>
  <div class="app-container">
    <a-skeleton :loading="loading">
      <a-result v-if="error === true" title="授权提醒" sub-title="首次使用时需要使用飞书授权哦，请授权后再试吧~">
        <template #icon>
          <a-icon type="safety-certificate" theme="twoTone" />
        </template>
        <template #extra>
          <a-button type="primary" @click="onReLogin"> 立即授权 </a-button>
        </template>
      </a-result>
      <div id="feishu-page" />
    </a-skeleton>
  </div>
</template>
<script>
import { setCookies } from '@/utils/utils';
import { getSignature } from '@/api/system/auth';

export default {
  props: {
    token: {
      type: String,
      default: null
    },
    titleVisible: {
      type: Boolean,
      default: true
    },
    everyoneEdit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      error: false
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.authPage();
    });
  },
  methods: {
    authPage() {
      getSignature({ url: `https://xjs3ti3gwc.feishu.cn/docx/${this.token}` })
        .then(response => {
          this.error = false;
          this.loading = false;
          const openId = response.data.openId;
          const signature = response.data.signature;
          const appId = response.data.appId;
          const timestamp = response.data.timestamp;
          const nonceStr = response.data.nonceStr;
          const url = response.data.url;
          const jsApiList = ['DocsComponent'];
          const that = this;
          window.webComponent
            .config({
              openId, // 当前登录用户的open id，要确保与生成 signature 使用的 user_access_token 相对应，使用 app_access_token 时此项不填。注意：仅云文档组件可使用app_access_token
              signature, // 签名
              appId, // 应用 appId
              timestamp, // 时间戳（毫秒）
              nonceStr, // 随机字符串
              url, // 第3步参与加密计算的url,同时也是最终需要访问的url
              jsApiList // 指定要使用的组件列表，请根据对应组件的开发文档填写。如云文档组件，填写['DocsComponent']
            })
            .then(res => {
              // 动态渲染，返回组件实例。
              const myComponent = window.webComponent.render(
                'DocsComponent',
                {
                  // 组件参数
                  src: url,
                  minHeight: '500px',
                  height: '1100px', // 该参数控制了页面的最大展开高度,不允许为Auto,也不能不写,会导致锚点定位,文本搜索失效
                  width: '100%'
                },
                document.querySelector('#feishu-page') // 将组件挂在到哪个元素上
              );
              // 通过setFeatureConfig方法修改组件的配置属性
              myComponent.config.setFeatureConfig({
                // 文档头部菜单栏
                HEADER: {
                  enable: true // 是否显示头部
                },
                // 点赞
                LIKE: {
                  enable: true // 是否显示点赞
                },
                // 图片
                IMAGE: {
                  maxWidth: 500 // 图片最大宽度
                },
                // 分享
                SHARE: {
                  enable: false, // 是否显示分享按钮
                  visibleConfig: {
                    // 区域的显隐配置
                    invite: false, // 邀请区域
                    shareLink: false, // 分享链接区域
                    shareMethod: false // 分享方式区域
                  }
                },
                // 模态窗
                MODAL: {
                  innerMask: true, // 有模态窗时， 组件内是否有遮罩
                  outerMask: {
                    enable: true, // 有模态窗时， 组件外是否有遮罩
                    zIndex: 1000 // 遮罩层的z-index
                  },
                  offset: {
                    // 模态窗的偏移量
                    x: 0,
                    y: 0
                  }
                },
                // 文档内容
                CONTENT: {
                  readonly: false, // 是否只读
                  padding: [0, 0, 0, 0], // 文档内边距，例：[10, 0, 10, 0] 为上下增加 10 px 边距
                  maxWidth: 0, // 内部最大宽度
                  titleVisible: this.titleVisible, // 标题是否可见
                  unscrollable: false // 云文档组件是都允许滚动
                },
                // 评论
                COMMENT: {
                  partial: {
                    // 局部评论
                    enable: true, // 是否启用局部评论
                    open: true // 是否展开局部评论侧栏
                  },
                  global: {
                    // 全局评论
                    enable: true // 是否启用全局评论
                  }
                },
                // 侧边栏
                SIDEBAR: {
                  borderSide: [false, false, false, false] // 侧边栏边框显隐，例：[true, true, false, false] 上、右侧有边框
                },
                // 目录
                DIRECTORY: {
                  enable: true, // 是否显示目录
                  pin: true // 是否固定目录
                },
                // 文档头部右侧更多菜单
                MORE_MENU: {
                  enable: true, // 是否显示更多菜单
                  items: {
                    // 菜单子项
                    findAndReplaceEnable: true, // 是否显示查找替换
                    makeCopyEnable: true, // 是否显示创建副本
                    applyEditPermissionEnable: false, // 是否显示申请编辑权限入口
                    exportEnable: false, // 是否显示导出
                    documentInfoEnable: true, // 是否显示文档详情
                    editHistoryEnable: true, // 是否显示编辑历史
                    commentHistoryEnable: true, // 是否显示评论历史
                    translateEnable: true, // 是否显示翻译
                    printEnable: true, // 是否显示打印
                    deleteEnable: false, // 是否显示删除
                    miniAppEnable: true // 是否显示小组件
                  }
                },
                // 右下角全屏按钮
                FULLSCREEN: {
                  enable: true // 是否显示按钮
                },
                // 文档头部右侧协作者列表
                COLLABORATOR: {
                  enable: true // 是否显示协作者列表
                }
              });
              myComponent.event.onTitleChange(title => {
                that.$emit('onTitleChange', title);
              });
            });
        })
        .catch(() => {
          this.error = true;
          this.loading = false;
        });
    },
    onReLogin() {
      localStorage.removeItem('token');
      localStorage.removeItem('user_info');
      localStorage.removeItem('refreshToken');
      setCookies('token'); // 清除token
      setCookies('refreshToken'); // 清除refreshToken
      window.location.href = process.env.VUE_APP_SSO_URL + '?url=' + window.location.href;
    }
  }
};
</script>
